import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Terminal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { BTN_PRIMARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, ROLE_LEVEL } from "@/lib/api";

const SRC = { server: "text-slate-300", ranger: "text-[#E5202E]" };

const useLogs = (cid) => {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    let live = true;
    let last = null;
    setLogs([]);
    const load = async () => {
      const q = last ? `?after=${encodeURIComponent(last)}` : "?limit=200";
      const { data } = await api.get(`/communities/${cid}/console/logs${q}`).catch(() => ({ data: [] }));
      if (!live || !data.length) return;
      last = data[data.length - 1].created_at;
      setLogs((prev) => [...prev, ...data].slice(-500));
    };
    load();
    const t = setInterval(load, 3000);
    return () => { live = false; clearInterval(t); };
  }, [cid]);
  return logs;
};

const LogView = ({ logs }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [logs]);
  if (!logs.length) return <EmptyState icon={Terminal} title="No console output" testId="console-empty">Logs stream here from the live WebRCON connection. Nothing is shown until the server sends real output.</EmptyState>;
  return (
    <div ref={ref} data-testid="console-log-view" role="log" aria-live="polite" className="h-[440px] overflow-y-auto rounded-sm border border-[#1E2D4A] bg-[#050811] p-4 font-mono text-xs leading-relaxed">
      {logs.map((l) => (
        <div key={l.id} className={`whitespace-pre-wrap break-words ${SRC[l.source] || "text-slate-400"}`}>
          <span className="text-slate-600">[{new Date(l.created_at).toLocaleTimeString()}]</span>{" "}
          {l.source === "ranger" ? `> ${l.message}  (${l.actor})` : l.message}
        </div>
      ))}
    </div>
  );
};

const CommandRunner = ({ cid, state }) => {
  const { level } = useAuth();
  const [list, setList] = useState([]);
  const [key, setKey] = useState("serverinfo");
  const [args, setArgs] = useState({});
  const [out, setOut] = useState(null);
  useEffect(() => { api.get("/console/allowlist").then((r) => setList(r.data)); }, []);
  const spec = list.find((c) => c.key === key);
  const allowed = spec && level >= ROLE_LEVEL[spec.min_role];
  const connected = state === "connected";

  const send = async () => {
    try {
      const { data } = await api.post(`/communities/${cid}/console/execute`, { command: key, args });
      setOut(data);
      toast.success(`Sent: ${data.command}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = (e) => { e.preventDefault(); if (!spec?.confirm) send(); };
  const disabled = !connected || !allowed;

  return (
    <form onSubmit={submit} className="space-y-4">
      <Select value={key} onValueChange={(v) => { setKey(v); setArgs({}); setOut(null); }}>
        <SelectTrigger data-testid="console-command-select" className="border-[#1E2D4A] bg-[#050811] font-mono"><SelectValue /></SelectTrigger>
        <SelectContent className="border-[#1E2D4A] bg-[#0B1120]">
          {list.map((c) => <SelectItem key={c.key} value={c.key} data-testid={`console-command-option-${c.key}`}>{c.label} · <span className="text-slate-500">{c.min_role}+</span></SelectItem>)}
        </SelectContent>
      </Select>
      {spec && <p className="text-xs text-slate-500">{spec.description}</p>}
      {spec?.args.map((a) => (
        <Input key={a.name} data-testid={`console-arg-${a.name}`} required maxLength={a.max || 40} placeholder={a.name.replace("_", " ")} value={args[a.name] || ""} onChange={(e) => setArgs({ ...args, [a.name]: e.target.value })} className="border-[#1E2D4A] bg-[#050811] font-mono" aria-label={a.name} />
      ))}
      {spec?.confirm ? (
        <ConfirmButton testId="console-send-button" className={BTN_PRIMARY} disabled={disabled} title={`Run ${spec.label}?`} description="This affects players on the live server and is recorded in the audit log." onConfirm={send}><Send className="h-4 w-4" />Send</ConfirmButton>
      ) : <button data-testid="console-send-button" disabled={disabled} className={BTN_PRIMARY}><Send className="h-4 w-4" />Send</button>}
      {!connected && <p data-testid="console-disabled-reason" className="text-xs text-amber-400">Commands are disabled: RCON is {state}.</p>}
      {connected && !allowed && <p className="text-xs text-amber-400">Requires {spec?.min_role} role.</p>}
      {out && <pre data-testid="console-command-response" className="max-h-48 overflow-auto whitespace-pre-wrap rounded-sm border border-[#1E2D4A] bg-[#050811] p-3 font-mono text-xs text-slate-300">{out.timed_out ? "No reply within 10s (command was sent)." : out.response || "(empty reply)"}</pre>}
    </form>
  );
};

export default function Console() {
  const { active } = useAuth();
  const logs = useLogs(active.id);
  const [state, setState] = useState("unconfigured");
  useEffect(() => {
    const load = () => api.get(`/communities/${active.id}/rcon/status`).then((r) => setState(r.data.state)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [active.id]);

  return (
    <div>
      <PageHeader eyebrow="WebRCON stream" title="Live Console" description="Only allowlisted commands with validated arguments can be sent. Every command is audited." actions={<StatusBadge state={state} testId="console-rcon-status" />} />
      <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
        <Card title="Server output" icon={Terminal}><LogView logs={logs} /></Card>
        <Card title="Safe commands"><CommandRunner cid={active.id} state={state} /></Card>
      </div>
    </div>
  );
}
